<!-- 标签导航栏 -->
<script setup lang="ts">
import router from '@/router'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import { onBeforeRouteUpdate, useRoute } from 'vue-router'
import { useUserInfoStore } from '@/stores/modules/user'
import type { NavTab } from '@/types/user'

const props = withDefaults(defineProps<{ homePath?: string; homeTitle?: string }>(), {
  homePath: '/admin/dashboard',
  homeTitle: '首页'
})

const userStore = useUserInfoStore()
// 路由
const route = useRoute()
// 当前选中的标签
const activeTab = ref(route.path)
// 当前打开的所有标签
const { tabList } = storeToRefs(userStore)

// 从 homePath 提取布局前缀（/admin 或 /customer）
const layoutPrefix = computed(() => {
  const match = props.homePath.match(/^\/[^/]+/)
  return match ? match[0] : '/'
})

// 当前布局下的标签（过滤掉其他布局的标签）
const scopedTabList = computed(() =>
  tabList.value.filter((t) => t.path.startsWith(layoutPrefix.value))
)

// 组件挂载时，清理不属于当前布局的标签
onMounted(() => {
  const hasOtherLayoutTabs = tabList.value.some((t) => !t.path.startsWith(layoutPrefix.value))
  if (hasOtherLayoutTabs) {
    tabList.value = tabList.value.filter((t) => t.path.startsWith(layoutPrefix.value))
  }
  // 确保首页标签存在
  if (!tabList.value.some((t) => t.path === props.homePath)) {
    tabList.value.unshift({ title: props.homeTitle, path: props.homePath })
  }
  // 确保当前页面的标签存在
  addTab({ title: route.meta.title + '', path: route.path })
})

// 跳转页面前将当前路由添加进缓存
onBeforeRouteUpdate((to) => {
  activeTab.value = to.path
  addTab({
    title: to.meta.title + '',
    path: to.path
  })
})

// 添加标签导航
const addTab = (tab: NavTab) => {
  // 与当前tabList中的tab进行比对
  let noTab = tabList.value.findIndex((t) => t.path == tab.path) == -1
  // 如果当前标签列表中没有则添加
  if (noTab) {
    tabList.value.push(tab)
  }
}

// 切换标签
const changeTab = (tab: string) => {
  router.push(tab)
}

// 移除标签
const removeTab = (tab: string) => {
  let tabs = scopedTabList.value
  let active = activeTab.value
  // 判断当前关闭的是否为当前选中的tab
  if (active == tab) {
    tabs.forEach((tabItem, index) => {
      // 遍历寻找当前关闭的标签索引
      if (tabItem.path === tab) {
        // 将选中的标签标记为 下一个 或者 前一个 tab
        const nextTab = tabs[index + 1] || tabs[index - 1]
        if (nextTab) {
          active = nextTab.path
        }
      }
    })
  }
  activeTab.value = active
  changeTab(active)
  // 筛选过滤去除掉关闭的标签
  tabList.value = tabList.value.filter((tabItem) => tabItem.path != tab)
}

// 关闭其它 全部关闭
const handleClose = (command: string) => {
  switch (command) {
    case 'clearOther':
      // 清除其它的
      tabList.value = tabList.value.filter(
        (tab) => tab.path == props.homePath || tab.path == activeTab.value
      )
      break
    case 'clearAll':
      // 切换回首页
      activeTab.value = props.homePath
      // 过滤只剩下首页
      tabList.value = [
        {
          title: props.homeTitle,
          path: props.homePath
        }
      ]
      changeTab(props.homePath)
      break
    default:
      break
  }
}
</script>

<template>
  <div class="app-label-nav">
    <div class="tags-item">
      <el-tag
        v-for="item in scopedTabList"
        :key="item.path"
        :closable="item.path != homePath"
        :effect="activeTab == item.path ? 'dark' : 'light'"
        @click="changeTab(item.path)"
        @close="removeTab(item.path)"
      >
        {{ item.title }}
      </el-tag>
    </div>
    <span class="tag-btn">
      <el-dropdown @command="handleClose">
        <span>
          <el-icon><ArrowDownBold /></el-icon>
        </span>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item command="clearOther">关闭其他</el-dropdown-item>
            <el-dropdown-item command="clearAll">全部关闭</el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </span>
  </div>
</template>
<style scoped lang="scss">
.app-label-nav {
  @apply flex justify-between items-center;
}

.tags-item {
  @apply flex items-center space-x-1.5 cursor-pointer;
  overflow-x: auto;
  flex: 1;
  min-width: 0;
  /* 隐藏滚动条 */
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.tags-item::-webkit-scrollbar {
  display: none;
}

.tag-btn {
  @apply flex items-center mr-2 flex-shrink-0;
}
</style>
